import * as React from 'react';
import type { GestureResponderEvent, StyleProp, Text, TextProps, TextStyle } from 'react-native';

import { Interactive } from '../Interactive';
import { ThemedStyleSheet, useCurrentTheme } from '../theme';
import { useLinkProps } from './useLinkProps';

export type LinkKind = 'default' | 'in-text' | 'in-text-muted' | 'blank' | 'bold';

export type LinkProps = TextProps & {
    kind?: LinkKind;

    /** URL for accessibility */
    url?: string;

    /** Only for web: should the link be open in the current tab ? */
    sameWebTab?: boolean;

    /** Custom handler for press events, it default to opening the url  */
    onPress?: (event: GestureResponderEvent) => void;

    children: React.ReactNode;
    style?: StyleProp<TextStyle>;
};

/**
 * Similar to a button but acting as inline text.
 */
export const Link = React.forwardRef(function LinkWithRef(
    props: LinkProps,
    ref: React.Ref<Text>
): React.ReactElement {
    const { url, kind = 'default', sameWebTab = false, children, onPress, style, ...rest } = props;
    const getLinkProps = useLinkProps();

    const { onPress: onPressHandler, ...linkProps } = getLinkProps(url, sameWebTab);

    const [, styles] = useCurrentTheme(themedKindStyles[kind]);

    return (
        <Interactive.Text
            ref={ref}
            {...rest}
            // @ts-ignore
            {...linkProps}
            onPress={(event) => {
                if (onPress) {
                    onPress(event);
                }
                onPressHandler(event);
            }}
            style={[styles.link, style]}
            activeStyle={styles.linkHovered}
        >
            {children}
        </Interactive.Text>
    );
});

const themedKindStyles = {
    default: ThemedStyleSheet.create({
        link: (theme) => ({
            color: theme.colors.primary.main,
        }),
        linkHovered: (theme) => ({
            color: theme.colors.primary.standout,
        }),
    }),
    'in-text': ThemedStyleSheet.create({
        link: (theme) => ({
            textDecorationLine: 'underline',
            color: theme.colors.primary.main,
        }),
        linkHovered: () => ({
            textDecorationLine: 'none',
        }),
    }),
    'in-text-muted': ThemedStyleSheet.create({
        link: (theme) => ({
            textDecorationLine: 'underline',
            color: theme.colors.text.main,
        }),
        linkHovered: (theme) => ({
            color: theme.colors.primary.standout,
        }),
    }),
    blank: ThemedStyleSheet.create({
        link: (theme) => ({
            color: theme.colors.text.main,
        }),
        linkHovered: (theme) => ({
            color: theme.colors.primary.main,
        }),
    }),
    bold: ThemedStyleSheet.create({
        link: (theme) => ({
            color: theme.colors.text.main,
        }),
        linkHovered: (theme) => ({
            color: theme.colors.text.standout,
        }),
    }),
};
